import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface BooruCredentials {
  username?: string
  apiKey?: string
  disabled?: boolean // Flag to temporarily disable credentials without deleting them
  /**
   * True once these credentials have also been pushed to the user's account
   * (POST /api/booru/credentials). This store is ALWAYS localStorage-backed —
   * credentials are written here first and only additionally mirrored to the
   * account when the user is logged in — so this is "local only" vs "local and
   * synced", not "local vs database".
   *
   * The settings modal previously read a `source === "database"` field that was
   * never defined on this type or ever written, so the storage line always
   * rendered the "local" branch regardless of whether a sync had happened.
   */
  syncedToAccount?: boolean
}

interface BooruCredentialsStore {
  credentials: Record<string, BooruCredentials>
  dismissedPrompts: Record<string, boolean> // Track which sources had prompt dismissed
  setCredentials: (booruId: string, creds: BooruCredentials) => void
  getCredentials: (booruId: string) => BooruCredentials | undefined
  removeCredentials: (booruId: string) => void
  toggleCredentials: (booruId: string) => void // Toggle disabled state
  clearAll: () => void
  dismissPrompt: (booruId: string) => void
  isPromptDismissed: (booruId: string) => boolean
  resetDismissed: (booruId: string) => void
}

export const useBooruCredentialsStore = create<BooruCredentialsStore>()(
  persist(
    (set, get) => ({
      credentials: {},
      dismissedPrompts: {},
      
      setCredentials: (booruId: string, creds: BooruCredentials) => {
        console.log('[v0] Saving credentials to localStorage:', { booruId, hasUsername: !!creds.username, hasApiKey: !!creds.apiKey })
        set((state) => ({
          credentials: {
            ...state.credentials,
            [booruId]: creds,
          },
          // Reset dismissed state when credentials are added
          dismissedPrompts: {
            ...state.dismissedPrompts,
            [booruId]: false,
          },
        }))
      },
      
      getCredentials: (booruId: string) => {
        const creds = get().credentials[booruId]
        // Return undefined if credentials are disabled
        if (creds?.disabled) {
          console.log('[v0] Credentials disabled for:', booruId)
          return undefined
        }
        console.log('[v0] Getting credentials from localStorage:', { booruId, found: !!creds })
        return creds
      },
      
      removeCredentials: (booruId: string) => {
        set((state) => {
          const newCreds = { ...state.credentials }
          delete newCreds[booruId]
          return { credentials: newCreds }
        })
      },

      toggleCredentials: (booruId: string) => {
        set((state) => {
          const creds = state.credentials[booruId]
          if (!creds) return state
          
          const newDisabledState = !creds.disabled
          console.log(`[v0] ${newDisabledState ? 'Disabling' : 'Enabling'} credentials for:`, booruId)
          
          return {
            credentials: {
              ...state.credentials,
              [booruId]: {
                ...creds,
                disabled: newDisabledState,
              },
            },
          }
        })
      },
      
      clearAll: () => {
        set({ credentials: {}, dismissedPrompts: {} })
      },

      dismissPrompt: (booruId: string) => {
        console.log('[v0] Dismissing credentials prompt for:', booruId)
        set((state) => ({
          dismissedPrompts: {
            ...state.dismissedPrompts,
            [booruId]: true,
          },
        }))
      },

      isPromptDismissed: (booruId: string) => {
        return get().dismissedPrompts[booruId] === true
      },

      resetDismissed: (booruId: string) => {
        set((state) => ({
          dismissedPrompts: {
            ...state.dismissedPrompts,
            [booruId]: false,
          },
        }))
      },
    }),
    {
      name: 'booru-credentials-storage',
      version: 3, // Increment version for disabled field
      migrate: (persistedState: any, version: number) => {
        console.log('[v0] Migrating credentials store from version', version)
        // Preserve existing credentials when migrating
        if (persistedState && persistedState.credentials) {
          return persistedState
        }
        return { credentials: {}, dismissedPrompts: {} }
      },
    }
  )
)
